/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Box from "components/generals/Box";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { HeaderEnum, InsuranceInterface } from "medicalsystem-shared";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormActionType } from "types/form.enum";
import { getNewInsuranceDataContent } from "utils/model.utils";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import InsuranceForm from "components/forms/InsuranceForm";

/**
 * Insurance table component for displaying and managing insurance provider records.
 * 
 * Features:
 * - Displays insurance provider information in sortable data table
 * - Columns: insurance name, additional details, action buttons
 * - Edit insurance functionality via modal with InsuranceForm
 * - Delete insurance with confirmation dialog and list update
 * - Permission-based access control for all operations
 * - Automatic list update after delete operations
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before delete operations
 * 
 * @param {TableProps<InsuranceInterface>} props - Table properties
 * @param {InsuranceInterface[]} props.modelList - Array of insurance records to display
 * @param {Function} props.setModelList - Callback to update insurance list after changes
 * 
 * @returns {JSX.Element} Insurance data table with edit/delete actions
 */
const InsuranceTable: FC<TableProps<InsuranceInterface>> = ({
  modelList,
  setModelList,
  ...props
}) => {
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceInterface>(
    getNewInsuranceDataContent()
  );
  const [activeEditInsuranceModal, setActiveEditInsuranceModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();

  const [_isLoading, _error, sendRequest] = useHttp();

  const openModalHandler = (insurance: InsuranceInterface) => {
    setSelectedInsurance(insurance);
    setActiveEditInsuranceModal(true);
  };

  const deleteHandler = (insuranceTemplate: InsuranceInterface) => {
    setSelectedInsurance(insuranceTemplate);

    console.log("sending request...");
    sendRequest<boolean, InsuranceInterface>(
      CoreAPI_URLs.INSURANCE_GENERAL_OPERATION,
      onInsuranceTemplateDeleteHandler,
      "DELETE",
      JSON.stringify(insuranceTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: insuranceTemplate.id }],
      { payload: insuranceTemplate, successMessage: t("insuranceTable.insuranceDeleted")},
    );
  };

  const onInsuranceTemplateDeleteHandler = (
    result: boolean,
    insuranceTemplate: InsuranceInterface
  ) => {
    const indexList = modelList.indexOf(insuranceTemplate);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  const getUITable = (list: InsuranceInterface[]) => {
    return {
      columns: [
        {
          Header: t("insuranceTable.name"),
          accessor: "name",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("insuranceTable.payerId"),
          accessor: "payerId",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("insuranceTable.phone"),
          accessor: "phone",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("insuranceTable.address"),
          accessor: "address",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("insuranceTable.city"),
          accessor: "city",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("insuranceTable.state"),
          accessor: "state",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("insuranceTable.actions"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                name="viewInsuranceButton"
                variant="contained"
                color="info"
                sx={{ marginRight: 1 }}
                onClick={() => {
                  console.log("selectedId:" + JSON.stringify(value));
                  const insurance = list.find((p) => p.id === value);
                  console.log("selectedInsurance:" + JSON.stringify(insurance));
                  if (insurance) {
                    setModalAction(FormActionType.MODIFY);
                    openModalHandler(insurance);
                  }
                }}
              >
                {t("insuranceTable.viewAction")}
              </Button>

              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.INSURANCE].DELETE]}
              >
                <ButtonDialog
                  name="delteInsuranceButton"
                  onAcceptHandle={() => {
                    const insurance = list.find((p) => p.id === value);
                    if (insurance) {
                      deleteHandler(insurance);
                    }
                  }}
                  buttonText={t("insuranceTable.deleteAction")}
                  title={t("insuranceTable.deleteConfirmation")}
                  description={t("insuranceTable.description")}
                  labelledbyID={"InsuranceDelete"}
                  describedbyID={"InsuranceDescriptionId"}
                />
              </PermissionWrapper>
            </>
          ),
        },
      ],
      rows: list,
    };
  };
  const dataTableData = useMemo(() => getUITable(modelList), [modelList]);

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.INSURANCE].CREATE]}
        >
          <Button
            name="newInsuranceButton"
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setModalAction(FormActionType.NEW);
              setSelectedInsurance(getNewInsuranceDataContent);
              setActiveEditInsuranceModal(true);
            }}
          >
            {t("insuranceForm.newInsurance")}
          </Button>
        </PermissionWrapper>
      </Box>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}/>
      <Modal
        open={activeEditInsuranceModal}
        onClose={() => {
          setActiveEditInsuranceModal(false);
        }}
      >
        <InsuranceForm
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedInsurance}
          onCloseAction={() => {
            setActiveEditInsuranceModal(false);
          }}
          isModal={true}
        />
      </Modal>
    </>
  );
};

export default InsuranceTable;
